import { useRepoReplicache } from './use-repo-replicache'
import { commitMutators } from '@basehub/data/replicache/mutators/repo-space/commit/mutators'
import { useReplicacheQueryV2 } from './use-replicache-query'
import { invariant } from '@epic-web/invariant'
import { useQuery } from '@tanstack/react-query'
import { getCommit } from '~/lib/commits/get-commit'
import type { CommitQueryResult } from '@basehub/data/replicache/index'
import { useRepoStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { getSelectedBranchOrCommitQueryKey } from '~/lib/commits/use-selected-branch-or-commit'

/**
 * @param isReferencedCommit - If true, the query to the current repo is skipped to save time and we look directly into the original template
 */
export const useCommit = (
  commitId: string | undefined | null,
  isReferencedCommit?: boolean
): {
  isLoading: boolean
  data: CommitQueryResult | undefined
} => {
  const { data, isLoading } = useRepoReplicache()
  const spaceId = useRepoStore((s) => s.spaceId)
  const { replicache } = data ?? {}
  const replicacheEnabled =
    commitId !== undefined && !isReferencedCommit && Boolean(replicache)

  const replicacheQuery = useReplicacheQueryV2<CommitQueryResult>({
    key: getSelectedBranchOrCommitQueryKey({
      playgroundId: undefined,
      spaceId,
      branchOrCommit: commitId ?? '<null-commit>',
    }),
    replicache,
    fetcher: async (tx) => {
      invariant(commitId !== undefined, 'commit id is undefined')
      if (!commitId) return null
      const commit = await commitMutators.getCommit(tx, commitId)
      if (!commit) return null
      return {
        result: commit,
        isFallback: false,
      }
    },
    enabled: replicacheEnabled,
  })

  const fallbackEnabled =
    !!spaceId && !isLoading && !replicacheEnabled && !replicacheQuery.data

  const fallback = useQuery<CommitQueryResult>(
    getSelectedBranchOrCommitQueryKey({
      playgroundId: undefined,
      spaceId,
      branchOrCommit: commitId ?? '<null-commit>',
      fallback: true,
    }),
    async () => {
      if (!commitId) return null
      invariant(spaceId, `spaceId is undefined, this should not happen.`)
      const referencedCommit = await getCommit({
        commitId,
        spaceId,
      })
      if (!referencedCommit) return null
      return {
        result: referencedCommit,
        isFallback: true,
      }
    },
    {
      staleTime: Infinity, // immutable
      enabled: fallbackEnabled,
    }
  )

  if (fallbackEnabled) {
    return fallback
  }

  return replicacheQuery
}
